import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "react-phone-number-input/style.css";
import JsonCollapse from "../../JsonCollapse";
import { proxyAPI } from "../../../../proxyAPI";
import FullScreenLoadingIndicator from "../../../../Common/FullScreenLoadingIndicator";
import ErrorBoundary from "../../ErrorBoundary";
import { Tab, Tabs } from "react-bootstrap";
import StyledTabContainer from "../../../../Common/StyledTabContainer";
import {
  InputArgument,
  ProjectData,
  StartFormData,
  VariableGroup,
} from "./types";
import { formatDateValue } from "./dateUtils";
import { getFieldError } from "./fieldValidation";
import InputArgumentField from "./InputArgumentField";
import StartButton from "./StartButton";
import ScheduleSettings from "../../../../Schedulers/ScheduleSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { getDefaultBusinessHours } from "../../BusinessHoursForm";

interface ProjectStartFormProps {
  projectID?: string | number;
  showHiddenVariables?: boolean;
  onChange?: (formData: StartFormData) => void;
  hideStartButton?: boolean;
  filterInputArguments?: (arg: InputArgument) => boolean;
  initialFormData?: StartFormData;
  initialInputArguments?: InputArgument[];
  fixedRoleValues?: Record<string, string>;
  initialVariableGroups?: VariableGroup[];
  showSchedule?: boolean;
}

const projectUuidFromProjectData = (projectData?: ProjectData) =>
  projectData?.UUID || projectData?.Uuid || projectData?.uuid || "";

const getDefaultValue = (arg: InputArgument) => {
  const defaultValue = [arg.Default, arg.DefaultValue].find(
    (value) => value !== null && value !== undefined && value !== ""
  );

  if (arg.Format === "business-hours") {
    if (typeof defaultValue === "string") {
      return defaultValue;
    }

    return JSON.stringify(defaultValue ?? getDefaultBusinessHours());
  }

  if (defaultValue === undefined) {
    return undefined;
  }

  if (arg.Format === "editor" && typeof defaultValue !== "string") {
    return JSON.stringify(defaultValue);
  }

  return String(defaultValue);
};

const getVariableGroupTabKey = (vg: VariableGroup) => String(vg.Order);

export default function ProjectStartForm({
  projectID,
  showHiddenVariables,
  onChange,
  hideStartButton,
  filterInputArguments,
  initialFormData = undefined,
  initialInputArguments = undefined,
  fixedRoleValues = undefined,
  initialVariableGroups = undefined,
  showSchedule = false,
}: ProjectStartFormProps) {
  const [projectData, setProjectData] = useState<ProjectData | undefined>();
  const inputArguments: InputArgument[] =
    initialInputArguments || projectData?.InputArguments || [];
  const variableGroups: VariableGroup[] =
    initialVariableGroups || projectData?.VariableGroups || [];
  const [formData, setFormData] = useState<StartFormData>(
    initialFormData || {}
  );
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [processGUID, setProcessGUID] = useState("");
  const [processID, setProcessID] = useState("");
  const [activeVariableGroupKey, setActiveVariableGroupKey] = useState("");

  const handleStart = async () => {
    setStarting(true);
    try {
      const sendData: { inputs?: Record<string, string[]> } = {};
      if (
        inputArguments.length > 0 &&
        Object.values(formData).filter((ia) => ia.find((v) => v !== ""))
          .length > 0
      ) {
        sendData.inputs = {};
        Object.keys(formData).forEach((key) => {
          if (formData[key].find((v) => v !== "")) {
            sendData.inputs![key] = formData[key].filter((v) => v !== "");
          }
        });
      }
      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `start-project/${projectID}?channel=manual`,
        method: "POST",
        body: sendData,
      });
      if (data) {
        setProcessGUID(data.GUID);
        setProcessID(data.ID);
        toast.info(data.message);
      }
    } catch (error) {
      console.error(
        "There has been a problem with your fetch operation:",
        error
      );
    }
    setStarting(false);
  };

  const getIsVisible = (
    vg: Pick<
      VariableGroup | InputArgument,
      "VisibilityField" | "VisibilityLogical" | "VisibilityValues"
    >
  ) =>
    !vg.VisibilityField ||
    inputArguments.some(
      (arg) =>
        arg.Field === vg.VisibilityField &&
        ((vg.VisibilityLogical === "is" &&
          (vg.VisibilityValues ?? []).includes(
            formData?.[arg.Field]?.[0] ?? ""
          )) ||
          (vg.VisibilityLogical === "is_not" &&
            !(vg.VisibilityValues ?? []).includes(
              formData?.[arg.Field]?.[0] ?? ""
            )) ||
          (vg.VisibilityLogical === "gte" &&
            formData?.[arg.Field]?.some((v) =>
              (vg.VisibilityValues ?? []).some(
                (value) => (value as number) <= parseFloat(v)
              )
            )) ||
          (vg.VisibilityLogical === "lte" &&
            formData?.[arg.Field]?.some((v) =>
              (vg.VisibilityValues ?? []).some(
                (value) => (value as number) >= parseFloat(v)
              )
            )))
    );

  const visibleVariableGroups = useMemo(
    () =>
      [...variableGroups]
        .sort(
          (a, b) =>
            (a.Order ?? Number.MAX_SAFE_INTEGER) -
            (b.Order ?? Number.MAX_SAFE_INTEGER)
        )
        .filter((vg) =>
          inputArguments.some(
            (arg) => arg.GroupUuid === vg.GroupUuid && arg.IsParameter
          )
        )
        .filter(getIsVisible),
    [variableGroups, inputArguments, formData]
  );

  const activeVariableGroupIndex = visibleVariableGroups.findIndex(
    (vg) => getVariableGroupTabKey(vg) === activeVariableGroupKey
  );

  const showVariableGroupNavigation = visibleVariableGroups.length > 1;

  const setActiveVariableGroupByOffset = (offset: number) => {
    const nextGroup = visibleVariableGroups[activeVariableGroupIndex + offset];

    if (nextGroup) {
      setActiveVariableGroupKey(getVariableGroupTabKey(nextGroup));
    }
  };

  // check if form has empty values for required fields
  const isStartDisabled = inputArguments
    .filter((arg) => arg.IsRequiredManual && getIsVisible(arg))
    .map((arg) => formData[arg.Field])
    .map((data) => !data || data.length === 0 || data.some((d) => !d))
    .some((item) => item);

  // fetch project data and reset process state
  useEffect(() => {
    setProcessGUID("");
    setProcessID("");
    if (!projectID) {
      return;
    }
    (async () => {
      const pd = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `/project/${projectID}`,
        setLoading,
        request: null,
      });
      setProjectData(pd);
    })();
  }, [projectID]);

  // set default values when project data loads
  useEffect(() => {
    if (inputArguments.length === 0) {
      return;
    }
    const fd = { ...formData };
    inputArguments.forEach((arg) => {
      if (fixedRoleValues && fixedRoleValues[arg.Role ?? ""]) {
        fd[arg.Field] = [fixedRoleValues[arg.Role ?? ""]];
        return;
      }
      if (!fd[arg.Field]) {
        const defaultValue = getDefaultValue(arg);
        if (defaultValue !== undefined) {
          fd[arg.Field] = [defaultValue];
          return;
        }
        if (["sms", "voice", "json"].includes(arg.Format)) {
          fd[arg.Field] = [""];
          return;
        }
        if (["number"].includes(arg.Format)) {
          fd[arg.Field] = arg.Default ? [String(arg.Default)] : ["0"];
          return;
        }
        if (["short-text", "email", "editor"].includes(arg.Format)) {
          if (arg.IsMultiple) {
            fd[arg.Field] = [];
            return;
          }
          fd[arg.Field] = [""];
          return;
        }
        if (["date", "time", "dateTime"].includes(arg.Format)) {
          fd[arg.Field] = [formatDateValue(new Date(), arg.Format)];
          return;
        }
        if (["configuration"].includes(arg.Format)) {
          if (arg.DataStructure === "Yes / No") {
            fd[arg.Field] = ["no"];
            return;
          }
          if (arg.DataStructure === "Duration") {
            fd[arg.Field] = ["0|second"];
            return;
          }
        }
      }
    });
    setFormData(fd);
  }, [projectData]);

  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData]);

  useEffect(() => {
    if (
      visibleVariableGroups.some(
        (vg) => getVariableGroupTabKey(vg) === activeVariableGroupKey
      )
    ) {
      return;
    }

    setActiveVariableGroupKey(
      visibleVariableGroups[0]
        ? getVariableGroupTabKey(visibleVariableGroups[0])
        : ""
    );
  }, [visibleVariableGroups, activeVariableGroupKey]);

  return (
    <ErrorBoundary>
      <FullScreenLoadingIndicator loading={loading} />
      <StyledTabContainer id="project-start-form-tabs">
        <Tabs
          activeKey={activeVariableGroupKey}
          onSelect={(key) => setActiveVariableGroupKey(key ?? "")}
        >
          {visibleVariableGroups.map((vg, _i, vgs) => (
            <Tab
              eventKey={getVariableGroupTabKey(vg)}
              key={getVariableGroupTabKey(vg)}
              className="mb-4"
              title={vgs.length > 1 ? vg.Label : undefined}
            >
              <div className="my-3">
                <div className="text-secondary">{vg.Description}</div>
              </div>
              {inputArguments
                .filter((arg) => arg.GroupUuid === vg.GroupUuid)
                .filter(filterInputArguments || (() => true))
                .filter((arg) => arg.IsParameter)
                .filter((arg) => arg.Format !== "data-entry")
                .filter(
                  (arg) =>
                    !["asset", "contact", "event", "ticket"].includes(
                      arg.Format
                    ) ||
                    showHiddenVariables
                )
                .filter(getIsVisible)
                .sort((a, b) => a.Order - b.Order)
                .map((arg) => (
                  <div
                    key={arg.UUID}
                    className="position-relative bg-light rounded p-3 border mb-3"
                  >
                    {!projectID && arg.Format === "editor" && (
                      <div className="position-absolute top-0 start-0 w-100 h-100 bg-secondary bg-opacity-50 z-3 px-2 py-1 rounded">
                        <div className="position-absolute top-50 start-50 translate-middle px-2 py-1 rounded">
                          Project needs to be deployed before editing message
                        </div>
                      </div>
                    )}
                    {fixedRoleValues && fixedRoleValues[arg.Role ?? ""] ? (
                      <div className="bg-secondary-subtle p-2 rounded border">
                        {fixedRoleValues[arg.Role ?? ""]}
                      </div>
                    ) : (
                      <div className="d-flex">
                        <div className="col-4">
                          <label className="form-label fw-bold">
                            {arg.Label}{" "}
                            {arg.IsMultiple ? (
                              <small className="badge bg-secondary">
                                multiple
                              </small>
                            ) : (
                              ""
                            )}{" "}
                            {arg.IsRequiredManual ? (
                              <small className="badge bg-secondary">
                                required
                              </small>
                            ) : (
                              ""
                            )}
                          </label>

                          {arg.Description && (
                            <div
                              className="text-muted"
                              style={{ whiteSpace: "pre-line" }}
                            >
                              {arg.Description}
                            </div>
                          )}
                        </div>
                        <div
                          className="flex-grow-1 overflow-auto"
                          style={{ minWidth: 0 }}
                        >
                          <InputArgumentField
                            arg={arg}
                            values={formData[arg.Field] ?? []}
                            onChange={(newValues) => {
                              console.log(newValues);
                              setFormData((prev) => ({
                                ...prev,
                                [arg.Field]: newValues,
                              }));
                            }}
                            inputArgumentsInEditor={inputArguments.filter(
                              (ia) => ia.InEditor
                            )}
                            showShortlinksButton
                            projectUuid={projectUuidFromProjectData(projectData)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </Tab>
          ))}
        </Tabs>
        {showVariableGroupNavigation && (
          <div className="d-flex justify-content-between align-items-center gap-2 my-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={activeVariableGroupIndex <= 0}
              onClick={() => setActiveVariableGroupByOffset(-1)}
            >
              <FontAwesomeIcon icon={faAngleLeft} className="me-2" />
              Previous
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={
                activeVariableGroupIndex === -1 ||
                activeVariableGroupIndex >= visibleVariableGroups.length - 1
              }
              onClick={() => setActiveVariableGroupByOffset(1)}
            >
              Next
              <FontAwesomeIcon icon={faAngleRight} className="ms-2" />
            </button>
          </div>
        )}
      </StyledTabContainer>

      {!hideStartButton && (
        <StartButton
          isStartDisabled={isStartDisabled}
          starting={starting}
          onStart={handleStart}
          processGUID={processGUID}
          processID={processID}
          projectID={projectID ?? ""}
        />
      )}

      {showSchedule && (
        <div className="mb-3">
          Or you can schedule this process to run automatically at specific
          times and intervals{" "}
          <ScheduleSettings
            key={projectData?.UUID}
            projectUuid={projectData?.UUID}
            button={<span className="text-decoration-underline">here</span>}
            triggerData={formData}
          />
        </div>
      )}

      <JsonCollapse json={inputArguments} />
      <JsonCollapse json={formData} />
      <JsonCollapse json={variableGroups} />
    </ErrorBoundary>
  );
}
